import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { LogIn, Loader2, Lock } from "lucide-react";
import Logo from "../components/Logo";
import Dashboard from "../components/admin/Dashboard";

function formatApiErrorDetail(detail) {
  if (detail == null) return "No se pudo iniciar sesión. Intenta nuevamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Admin() {
  const { user, loading, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Credenciales inválidas");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-jauja-bg">
        <Logo height={44} className="animate-pulse" />
      </div>
    );
  }

  if (user) {
    return <Dashboard />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-jauja-bg px-5" data-testid="admin-login">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-jauja-surface p-10">
        <div className="flex justify-center">
          <Logo height={48} />
        </div>
        <p className="mt-4 text-center text-sm text-white/50">Panel de administración</p>
        <h1 className="mt-5 text-center font-display text-3xl text-white">Inicia sesión</h1>

        <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email"
              autoComplete="username"
              className="mt-2 w-full rounded-lg border border-white/15 bg-jauja-bg px-4 py-3 text-white outline-none transition-colors focus:border-jauja-lime placeholder:text-white/30"
              placeholder="tu@correo.com"
              required
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40">Contraseña</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              autoComplete="current-password"
              className="mt-2 w-full rounded-lg border border-white/15 bg-jauja-bg px-4 py-3 text-white outline-none transition-colors focus:border-jauja-lime placeholder:text-white/30"
              placeholder="••••••••"
              required
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="login-error">
              <Lock size={15} /> {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-jauja-lime px-6 py-4 text-sm font-bold text-jauja-bg transition-transform duration-200 hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
          >
            {submitting ? <Loader2 size={18} className="animate-spin" /> : <LogIn size={18} />}
            {submitting ? "Ingresando…" : "Ingresar"}
          </button>
        </form>

        <a href="/" className="mt-6 block text-center text-xs text-white/40 hover:text-jauja-lime">← Volver al sitio</a>
      </div>
    </div>
  );
}
